// src/components/Shared/SecurityBadge/SecurityBadge.tsx
import React from 'react';
import { Shield, Lock, Trash2, Eye, Cpu, LucideIcon } from 'lucide-react';

export type BadgeType = 'local' | 'secure' | 'encrypted' | 'deleted' | 'private';

interface BadgeConfig {
    icon: LucideIcon;
    label: string;
    color: string;
    tooltip: string;
}

interface SecurityBadgeProps {
    type?: BadgeType;
    showLabel?: boolean;
    customLabel?: string;
    size?: 'small' | 'md' | 'large'; // Added large just in case, though md was in the original
}

/**
 * SecurityBadge Component
 * Provides visual trust signals for different types of file processing.
 */
const SecurityBadge: React.FC<SecurityBadgeProps> = ({
    type = 'secure',
    showLabel = true,
    customLabel,
    size = 'md'
}) => {
    const badgeConfig: Record<BadgeType, BadgeConfig> = {
        local: {
            icon: Cpu,
            label: '100% Client-Side',
            color: 'blue',
            tooltip: 'Processed entirely in your browser. Files never leave your device.'
        },
        secure: {
            icon: Shield,
            label: 'Securely Processed',
            color: 'green',
            tooltip: 'Processed using enterprise-grade security protocols.'
        },
        encrypted: {
            icon: Lock,
            label: 'End-to-End Encrypted',
            color: 'purple',
            tooltip: 'Files are encrypted during transit and processing.'
        },
        deleted: {
            icon: Trash2,
            label: 'Auto-Deleted',
            color: 'orange',
            tooltip: 'Files are automatically deleted from memory immediately after processing.'
        },
        private: {
            icon: Eye,
            label: 'Privacy Protected',
            color: 'teal',
            tooltip: 'We never store, share, or peek into your processed data.'
        }
    };

    const config = badgeConfig[type] || badgeConfig.secure;
    const Icon = config.icon;

    // Base classes
    const baseClasses = "inline-flex items-center rounded-full font-medium transition-all duration-200 cursor-help select-none border border-transparent hover:brightness-95 hover:border-black/10 dark:hover:brightness-110 dark:hover:border-white/20";

    // Size classes
    const sizeClasses = size === 'small'
        ? "gap-1.5 py-0.5 px-2 text-xs"
        : "gap-2 py-1.5 px-3.5 text-[13px]";

    // Color classes (matching the specific hex values from the original CSS)
    const colorClasses = {
        blue: "bg-[#e8f0fe] text-[#174ea6] dark:bg-[#1976d2]/20",
        green: "bg-[#e6f4ea] text-[#137333] dark:bg-[#2e7d32]/20",
        purple: "bg-[#f3e8fd] text-[#7026b8] dark:bg-[#7b1fa2]/20",
        orange: "bg-[#feefe3] text-[#a15700] dark:bg-[#e65100]/20",
        teal: "bg-[#e2f2f1] text-[#00695c] dark:bg-[#00796b]/20"
    }[config.color as 'blue' | 'green' | 'purple' | 'orange' | 'teal'];

    return (
        <div
            className={`${baseClasses} ${sizeClasses} ${colorClasses}`}
            title={config.tooltip}
            role="status"
            aria-label={showLabel ? `${config.label}: ${config.tooltip}` : config.tooltip}
        >
            <Icon size={size === 'small' ? 14 : 16} strokeWidth={2} aria-hidden="true" />
            {showLabel && <span>{customLabel || config.label}</span>}
        </div>
    );
};

export default SecurityBadge;
